
import { useState } from 'react';
import { 
  Sidebar, 
  SidebarContent, 
  SidebarHeader, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem, 
  SidebarProvider,
  SidebarInset,
  SidebarTrigger
} from "@/components/ui/sidebar";
import { Users, Calendar, MessageSquare, DollarSign, BarChart2, Search, ShoppingBag, Wallet, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import CreatorRosterPanel from '@/components/admin/creator-management/CreatorRosterPanel';
import BookingManager from '@/components/admin/creator-management/BookingManager';
import CommunicationsHub from '@/components/admin/creator-management/CommunicationsHub';
import EarningsPayouts from '@/components/admin/creator-management/EarningsPayouts';
import AnalyticsReports from '@/components/admin/creator-management/AnalyticsReports';
import CreatorEcommerce from '@/components/creator/CreatorEcommerce';
import CreatorWallet from '@/components/creator/CreatorWallet';
import PortalIdBadge from '@/components/admin/PortalIdBadge';
import RepresentationRequests from '@/components/admin/RepresentationRequests';

type View = 'roster' | 'requests' | 'bookings' | 'comms' | 'earnings' | 'analytics' | 'shop' | 'wallet';

const CreatorManagementDashboard = () => {
  const [activeView, setActiveView] = useState<View>('roster');
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Logged out",
      description: "You have been successfully logged out.",
    });
    navigate("/landing-page-2");
  };

  const navItems = [
    { id: 'roster', label: 'Creator Roster', icon: Users, component: <CreatorRosterPanel /> },
    { id: 'bookings', label: 'Booking Manager', icon: Calendar, component: <BookingManager /> },
    { id: 'requests', label: 'Representation Requests', icon: MessageSquare, component: <RepresentationRequests /> },
    { id: 'comms', label: 'Communications', icon: MessageSquare, component: <CommunicationsHub /> },
    { id: 'earnings', label: 'Earnings & Payouts', icon: DollarSign, component: <EarningsPayouts /> },
    { id: 'analytics', label: 'Analytics', icon: BarChart2, component: <AnalyticsReports /> },
    { id: 'shop', label: 'Shop Management', icon: ShoppingBag, component: <CreatorEcommerce /> },
    { id: 'wallet', label: 'Wallet Management', icon: Wallet, component: <CreatorWallet /> },
  ];

  const activeMenuItem = navItems.find(item => item.id === activeView);

  return (
    <div className="min-h-screen bg-black text-white">
      <SidebarProvider>
        <div className="flex w-full min-h-screen">
          <Sidebar className="border-r border-white/20 bg-white text-black">
            <SidebarHeader className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <img 
                  src="/lovable-uploads/metro-city-groove-logo-white.png" 
                  alt="MCG Agency Logo" 
                  className="w-12 h-12 object-contain bg-white rounded-lg p-1 invert text-black"
                />
                <div>
                  <h1 className="text-lg font-bold text-black">Creator Management</h1>
                  <p className="text-xs text-gray-600">Admin Dashboard</p>
                </div>
              </div>
            </SidebarHeader>
            
            <SidebarContent className="p-4">
              <SidebarMenu>
                {navItems.map((item) => (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      onClick={() => setActiveView(item.id as View)}
                      isActive={activeView === item.id}
                      className={`w-full justify-start text-left px-4 py-3 rounded-lg transition-colors ${
                        activeView === item.id 
                          ? 'bg-gray-200 text-black' 
                          : 'text-black hover:bg-gray-100 hover:text-black'
                      }`}
                    >
                      <item.icon className="w-5 h-5 mr-3 text-black" />
                      <span className="text-sm text-black">{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarContent>
          </Sidebar>

          <SidebarInset className="flex-1 bg-black">
            <header className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b border-white/20 bg-black/95 backdrop-blur-md px-6">
              <SidebarTrigger className="text-white hover:bg-white/10" />
              <div className="flex items-center gap-2">
                {activeMenuItem && (
                  <>
                    <activeMenuItem.icon className="w-5 h-5 text-white" />
                    <h2 className="text-xl font-semibold text-white">{activeMenuItem.label}</h2>
                  </>
                )}
              </div>
              <div className="ml-auto flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search creators, bookings..."
                    className="bg-white/10 border border-white/20 rounded-lg pl-10 pr-4 py-2 w-64 text-white placeholder-gray-400 focus:ring-2 focus:ring-white/50 focus:outline-none"
                  />
                </div>
                <PortalIdBadge portalCode="CM" />
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </div>
            </header>

            <main className="flex-1 p-6">
              {activeMenuItem?.component}
            </main>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </div>
  );
};

export default CreatorManagementDashboard;
